// ============================================================
// xScan - Badge del modo de escaneo
// ============================================================
// Comunica cuanto sabia el escaneo del objetivo. Es el dato que separa
// "lo detectamos desde afuera" de "lo confirmamos desde adentro" — la
// diferencia que le importa a un carrier al tarifar.
// ============================================================
import { Eye, EyeOff, ScanSearch } from 'lucide-react';

export type ScanMode = 'blackbox' | 'greybox' | 'whitebox';

const MODES: Record<ScanMode, { label: string; hint: string; cls: string; icon: typeof Eye }> = {
  blackbox: {
    label: 'Caja negra',
    hint: 'Sin conocimiento previo del objetivo — solo lo visible desde Internet',
    cls: 'bg-gray-100 text-gray-700 border-gray-300',
    icon: EyeOff,
  },
  greybox: {
    label: 'Caja gris',
    hint: 'Con contexto parcial aportado: credenciales de prueba, specs de API o inventario de red',
    cls: 'bg-blue-50 text-blue-700 border-blue-300',
    icon: ScanSearch,
  },
  whitebox: {
    label: 'Caja blanca',
    hint: 'Con acceso de solo lectura a configuracion cloud o codigo fuente',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    icon: Eye,
  },
};

interface Props {
  mode?: string | null;
  /** Muestra la explicacion al lado del badge. */
  showHint?: boolean;
  size?: 'sm' | 'md';
}

export default function ScanModeBadge({ mode, showHint = false, size = 'md' }: Props) {
  const key: ScanMode = mode === 'greybox' || mode === 'whitebox' ? mode : 'blackbox';
  const meta = MODES[key];
  const Icon = meta.icon;
  const pad = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';

  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${pad} ${meta.cls}`}
        title={meta.hint}
      >
        <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
        {meta.label}
      </span>
      {showHint && <span className="text-xs text-gray-500">{meta.hint}</span>}
    </span>
  );
}
